/**
 * Webhook Config Dialog
 *
 * Create/Edit dialog for webhook URL configuration.
 * Per Design 5.1: Dialog with merchant selector and HTTPS-validated URL input.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Input,
  Label,
  Button,
} from '@/components/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useMerchantMap, useMerchantScope } from '@/hooks';
import { cn } from '@/lib/utils';

interface WebhookConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  merchantId?: string;
  currentUrl?: string;
  onSubmit: (merchantId: string, webhookUrl: string) => void;
  isLoading?: boolean;
}

export function WebhookConfigDialog({
  open,
  onOpenChange,
  mode,
  merchantId: initialMerchantId = '',
  currentUrl = '',
  onSubmit,
  isLoading = false,
}: WebhookConfigDialogProps) {
  const { t } = useTranslation('webhook-config');
  const [merchantId, setMerchantId] = useState(initialMerchantId);
  const [webhookUrl, setWebhookUrl] = useState(currentUrl);
  const [urlError, setUrlError] = useState('');
  const [merchantError, setMerchantError] = useState('');
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const { merchants, resolveMerchantName } = useMerchantMap();
  // Spec 0220: Merchant-Rollen sind tenant-gebunden — kein Merchant-Selector.
  const { showMerchant, ownMerchantId } = useMerchantScope();
  // Fail-safe: Merchant-Rolle ohne Tenant-Bindung darf keinen Webhook anlegen.
  const merchantContextMissing = mode === 'create' && !showMerchant && !ownMerchantId;

  useEffect(() => {
    if (open) {
      setMerchantId(initialMerchantId);
      setWebhookUrl(currentUrl);
      setUrlError('');
      setMerchantError('');
    }
  }, [open, initialMerchantId, currentUrl]);

  const validate = (): boolean => {
    let valid = true;

    // Spec 0220: Merchant-Pflichtfeld nur fuer Platform-Rollen (sichtbares Feld).
    // Merchant-Rollen erben ownMerchantId; der Fail-safe deckt den null-Fall.
    if (mode === 'create' && showMerchant && !merchantId.trim()) {
      setMerchantError(t('form.validation.merchantRequired'));
      valid = false;
    } else {
      setMerchantError('');
    }

    if (!webhookUrl.trim()) {
      setUrlError(t('form.validation.urlRequired'));
      valid = false;
    } else {
      try {
        const parsed = new URL(webhookUrl);
        if (parsed.protocol !== 'https:') {
          setUrlError(t('form.validation.urlHttps'));
          valid = false;
        } else {
          setUrlError('');
        }
      } catch {
        setUrlError(t('form.validation.urlRequired'));
        valid = false;
      }
    }

    return valid;
  };

  const handleSubmit = () => {
    // Spec 0220 Fail-safe: Merchant-Rolle ohne Tenant-Bindung → kein Create.
    if (merchantContextMissing) return;
    if (!validate()) return;
    // Spec 0220: Edit nutzt den bestehenden Tenant; im Create sendet Platform
    // das gewaehlte Feld, die Merchant-Rolle ihren eigenen Tenant.
    const targetMerchant =
      mode === 'edit'
        ? initialMerchantId
        : showMerchant
          ? merchantId
          : (ownMerchantId ?? '');
    onSubmit(targetMerchant, webhookUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('form.title.create') : t('form.title.edit')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? t('form.description.create', {
                  defaultValue: 'Configure a webhook endpoint for a merchant.',
                })
              : t('form.description.edit', {
                  defaultValue: 'Update the webhook URL for this merchant.',
                })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Merchant ID — Spec 0220: Platform waehlt im Create, Edit zeigt den */}
          {/* bestehenden Tenant readonly; Merchant-Rollen sehen das Feld nicht */}
          {/* (Tenant ist implizit aus dem Login). */}
          {(mode === 'edit' || showMerchant) && (
          <div className="space-y-2">
            <Label htmlFor="merchantId">{t('form.label.merchantId')}</Label>
            {mode === 'create' ? (
              <>
                <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="merchantId"
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboboxOpen}
                      className="w-full justify-between font-normal"
                    >
                      {merchantId
                        ? resolveMerchantName(merchantId)
                        : t('form.placeholder.selectMerchant', { defaultValue: 'Merchant auswählen...' })}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t('form.placeholder.searchMerchant', { defaultValue: 'Merchant suchen...' })} />
                      <CommandList>
                        <CommandEmpty>{t('form.noMerchant', { defaultValue: 'Kein Merchant gefunden.' })}</CommandEmpty>
                        <CommandGroup>
                          {merchants.map((m) => (
                            <CommandItem
                              key={m.merchantId}
                              value={`${m.name} ${m.merchantId}`}
                              onSelect={() => {
                                setMerchantId(m.merchantId);
                                setMerchantError('');
                                setComboboxOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  merchantId === m.merchantId ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <div>
                                <div className="font-medium">{m.name}</div>
                                <div className="text-xs text-muted-foreground font-mono">{m.merchantId}</div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {merchantError && (
                  <p className="text-sm text-destructive">{merchantError}</p>
                )}
              </>
            ) : (
              <p className="text-sm font-mono text-muted-foreground">{initialMerchantId}</p>
            )}
          </div>
          )}

          {/* Spec 0220 Fail-safe: Merchant-Rolle ohne Tenant-Bindung. */}
          {merchantContextMissing && (
            <p role="alert" className="text-sm text-destructive">
              {t('form.validation.merchantContextMissing')}
            </p>
          )}

          {/* Webhook URL */}
          <div className="space-y-2">
            <Label htmlFor="webhookUrl">{t('form.label.webhookUrl')}</Label>
            <Input
              id="webhookUrl"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder={t('form.placeholder.webhookUrl')}
            />
            {urlError && (
              <p className="text-sm text-destructive">{urlError}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('form.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || merchantContextMissing}>
            {isLoading
              ? t('form.saving', { defaultValue: 'Saving...' })
              : t('form.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
